'use client'

import { useState, useCallback, useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@/hooks/use-theme'
import { AuthContext } from '@/hooks/use-auth'
import { type User, type Organization, type ImpersonatingAdmin } from '@/lib/api'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import type { PostHog } from 'posthog-js'
import { ErrorBoundary, sentryEnabled } from '@/lib/sentry'
import { setLocale } from '@/lib/locale'
import { scrubAnalyticsEvent } from '@/lib/analytics-scrub'
import { shouldRetryQuery } from '@/lib/session-expiry'

// PostHog loads on idle (dynamic import = separate chunk, out of the main
// bundle). It used to init at module load, which injected extension scripts
// into <body> before React hydrated and broke hydration on every page —
// and shipped ~100 KB to marketing pages that never use analytics.
const POSTHOG_KEY = 'phc_yY8p1JbFEf5yqooCiYju9R8xUaGJjtr2HRXL3JBX5no'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: shouldRetryQuery,
    },
  },
})

const neverChanges = () => () => {}
const isClient = () => true
const isServer = () => false

export function Providers({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [impersonatingAdmin, setImpersonatingAdmin] = useState<ImpersonatingAdmin | null>(null)
  const [impersonationExpiresAt, setImpersonationExpiresAt] = useState<string | null>(null)
  // False in the server render and in hydration, then true.
  const mounted = useSyncExternalStore(neverChanges, isClient, isServer)
  const [phClient, setPhClient] = useState<PostHog | null>(null)
  const phRef = useRef<PostHog | null>(null)
  useEffect(() => {
    let cancelled = false
    const start = () => {
      import('posthog-js').then(({ default: ph }) => {
        if (cancelled) return
        ph.init(POSTHOG_KEY, {
          api_host: 'https://eu.i.posthog.com',
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: true,
          // Page view URLs carry the magic-link token and email: send the path only.
          before_send: (event) => scrubAnalyticsEvent(event),
          session_recording: {
            // Users paste webhook URLs, bot tokens and API keys into this
            // app: never record what they type.
            maskAllInputs: true,
          },
        })
        phRef.current = ph
        setPhClient(ph)
      })
    }
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(start, { timeout: 3000 })
      return () => {
        cancelled = true
        window.cancelIdleCallback(id)
      }
    }
    const t = setTimeout(start, 1)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [])

  // Identify once both the session user and the PostHog client exist —
  // either may arrive first (restore runs at mount, client loads on idle).
  useEffect(() => {
    if (phClient && user) {
      phClient.identify(user.id, { email: user.email, name: user.name })
    }
  }, [phClient, user])

  // Try to restore session on mount by checking /api/auth/me
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const result = await apiV2.getCurrentUser()
        setUser(result.user as User)
        setOrganization(result.organization)
        setImpersonatingAdmin(result.impersonating_admin ?? null)
        setImpersonationExpiresAt(result.impersonation_expires_at ?? null)
        // Identify happens in the effect below once the PostHog client
        // loads (session replay linking).
        if (result.user) {
          if (result.user.preferred_locale) {
            // Use reload:false — this is just syncing server preference into the cookie.
            // Reloading here would conflict with the user's manually-set cookie locale.
            setLocale(result.user.preferred_locale, { reload: false })
          }
        }
      } catch (e) {
        if (e instanceof ApiV2Error && e.status === 401) {
          // No valid session - expected when not logged in
        } else {
          toast.error('Unable to reach the server. Some features may not work.')
        }
      } finally {
        setIsLoading(false)
      }
    }
    restoreSession()
  }, [])

  const login = useCallback(async (email: string, password: string, totpCode?: string) => {
    const result = await apiV2.login(email, password, totpCode)

    if ('totp_required' in result && result.totp_required) {
      // Throw a typed error so the login page can show the TOTP form
      const err = new Error('2FA required') as Error & { totpRequired: true }
      err.totpRequired = true
      throw err
    }

    const authResult = result as { user: User; organization: Organization }
    setUser(authResult.user)
    setOrganization(authResult.organization)
    if (authResult.user.preferred_locale) {
      setLocale(authResult.user.preferred_locale)
    }
  }, [])

  const register = useCallback(async (name: string, email: string, password: string) => {
    const result = await apiV2.register(name, email, password)
    setUser(result.user as User)
    setOrganization(result.organization)
  }, [])

  const logout = useCallback(async () => {
    await apiV2.logout()
    setUser(null)
    setOrganization(null)
    setImpersonatingAdmin(null)
    setImpersonationExpiresAt(null)
    queryClient.clear()
    phRef.current?.reset()
  }, [])

  const refreshAuth = useCallback(async () => {
    const result = await apiV2.getCurrentUser()
    setUser(result.user as User)
    setOrganization(result.organization)
    setImpersonatingAdmin(result.impersonating_admin ?? null)
    setImpersonationExpiresAt(result.impersonation_expires_at ?? null)
    return result.organization
  }, [])

  const body = (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthContext.Provider
          value={{
            user,
            organization,
            isLoading,
            isAuthenticated: !!user,
            impersonatingAdmin,
            impersonationExpiresAt,
            login,
            register,
            logout,
            refreshAuth,
          }}
        >
          {children}
        </AuthContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  )
  // PostHog context is intentionally not mounted: nothing consumes
  // usePostHog, and mounting PostHogProvider would statically bundle
  // posthog-js into main. Analytics calls go through phRef once the
  // idle-loaded client is ready.
  const withAnalytics = body

  // Only wrap with ErrorBoundary after mount — sentry is unavailable on the
  // server, so wrapping conditionally would cause a hydration mismatch (React #418).
  return mounted && sentryEnabled ? (
    <ErrorBoundary fallback={<>{withAnalytics}</>}>{withAnalytics}</ErrorBoundary>
  ) : (
    withAnalytics
  )
}
